import * as classnames from "classnames";
import { isEqual } from "lodash";
import * as React from "react";

import {
  ApplicantExamHeader,
  ApplicantExamStatusDisplay,
  ApplicantChallengeSet,
  ApplicantChallenge,
  Portal,
} from "@shared/components";
import { ApplicantExamModel } from "@shared/models";

import SubmitExam from "../../../modals/submitExam/SubmitExam.connect";
import { HelpSection } from "../helpSection/HelpSection";
import { SubmitExamAlert } from "../submitExamAlert/SubmitExamAlert";

/**
 * Prop interface
 */
export interface EditorChallengeListProps {
  tutorial?: boolean;
  exam: ApplicantExamModel;
  submitExam: () => void;
}

/**
 * State interface
 */
export interface EditorChallengeListState {
  isOpenSubmitExam: boolean;
}

/**
 * Page component
 */
class EditorChallengeList extends React.Component<
  EditorChallengeListProps,
  EditorChallengeListState
> {
  constructor(props: EditorChallengeListProps) {
    super(props);

    this.state = {
      isOpenSubmitExam: false,
    };
  }

  public shouldComponentUpdate(
    nextProps: EditorChallengeListProps,
    nextState: EditorChallengeListState,
  ) {
    return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
  }

  public componentDidMount() {
    window.scrollTo(0, 0);
  }

  public render() {
    const { exam, tutorial } = this.props;

    const rootStyle = classnames("code-editor-challenge-list", {
      "is-submitted": !exam.canSubmit(),
    });

    const challengeSetContents = exam.challengesSets.map((challengeSet) => {
      if (challengeSet.isOptionalSet) {
        return (
          <ApplicantChallengeSet
            key={challengeSet.id}
            challengeSetId={challengeSet.id}
            exam={exam}
            tutorial={tutorial}
          />
        );
      } else {
        return challengeSet.challenges.map((challenge) => (
          <ApplicantChallenge
            key={challenge.id}
            challengeId={challenge.id}
            exam={exam}
            tutorial={tutorial}
          />
        ));
      }
    });

    return (
      <>
        <div className={rootStyle}>
          <div className="code-editor-challenge-list__container">
            {exam.areAllChallengesFinished() && (
              <SubmitExamAlert className="code-editor-challenge-list__alert" />
            )}

            <div className="code-editor-challenge-list__header-container">
              <div className="code-editor-challenge-list__main">
                <ApplicantExamHeader
                  tutorial={tutorial}
                  orgImageUrl={exam.org.imageUrl}
                  orgName={exam.org.displayName}
                  title={exam.exam.name}
                />
              </div>
            </div>
            <section className="code-editor-challenge-list__challenge-set">
              {challengeSetContents}
            </section>
          </div>
          <div className="code-editor-challenge-list__aside">
            <div className="code-editor-challenge-list__aside__sticky">
              <ApplicantExamStatusDisplay
                exam={exam}
                onClickSubmit={this.onOpenExamSubmit}
              />
              <HelpSection exam={exam} tutorial={tutorial} />
            </div>
          </div>
        </div>
        {this.state.isOpenSubmitExam && (
          <Portal>
            <SubmitExam
              isOpen={this.state.isOpenSubmitExam}
              onCancel={this.onCloseModal}
              onSubmit={this.onExamSubmit}
            />
          </Portal>
        )}
      </>
    );
  }

  private onOpenExamSubmit = () => {
    this.setState({ isOpenSubmitExam: true });
  };

  private onExamSubmit = () => {
    this.props.submitExam();
    this.onCloseModal();
  };

  private onCloseModal = () => {
    this.setState({ isOpenSubmitExam: false });
  };
}

export default EditorChallengeList;
